import React, { useState, useEffect } from "react";
import axios from "axios";
import { Nav, SpineRail, Footer } from "../components/site/Chrome";
import { Reveal, Eyebrow } from "../components/site/primitives";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { Maximize2, Clock, AlertTriangle, CheckCircle2, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "MedicalProcedure",
  "name": "Open TLIF (Transforaminal Lumbar Interbody Fusion)",
  "alternateName": "Open TLIF",
  "description": "Open TLIF is the traditional, most established way of performing transforaminal lumbar interbody fusion, using a single midline incision for full, direct visualization of the affected spine level.",
  "bodyLocation": "Lumbar spine",
  "performedBy": { "@type": "Physician", "name": "Dr. Amit Kumar Chanduka", "medicalSpecialty": "https://schema.org/Neurosurgery" },
  "areaServed": { "@type": "City", "name": "Bhubaneswar" }
};

const REASONS = [
  "Complex spinal deformity or significant curvature needing correction",
  "Revision surgery, where scar tissue from a previous operation makes minimally invasive access unsafe",
  "Multi-level fusion, where several segments need to be treated in the same operation",
  "Unusual or difficult anatomy where clear, direct visualization improves safety",
  "Cases where spinal instability is severe enough that maximum surgical control is the priority",
];
const STEPS = [
  "You lie face-down on the operating table.",
  "A midline incision is made in the lower back.",
  "The back muscles are gently retracted on either side to expose the affected vertebrae directly.",
  "The damaged disc is removed under direct vision, and the nerve is fully decompressed.",
  "A spacer (cage), usually packed with bone graft, is placed in the disc space to restore height and support.",
  "Pedicle screws and rods are placed into the vertebrae above and below to hold the segment stable while it fuses.",
];
const BEFORE = [
  "Blood tests, and an ECG or chest X-ray if advised, to confirm you are fit for anaesthesia",
  "Some regular medicines (especially blood thinners) may need to be paused — you will get exact instructions",
  "You will be asked to stop eating and drinking for a set number of hours before surgery",
  "Arrange for a family member to be with you, and for help at home for the first 2\u20133 weeks after you return",
];
const RECOVERY = [
  ["Day 1\u20133", "You are helped to sit up and take short, supported walks. Pain is managed with medication. Hospital stay is typically 3\u20135 days."],
  ["Week 1\u20133", "Rest at home with short, frequent walks. Avoid bending, twisting, or lifting anything heavier than about 2 kg. A back brace is often advised during this period."],
  ["Week 4\u20136", "Walking distance increases gradually. Light household activity resumes. Driving is usually allowed once movement is comfortable and you are off strong pain medication."],
  ["Month 2\u20133", "Physiotherapy typically begins to rebuild core and back strength. Desk-based work often resumes around this stage."],
  ["Month 3\u201312", "Bone fusion continues to strengthen, checked with follow-up X-rays. Full activity, including more demanding work, is guided by how solid the fusion looks and how you feel."],
];
const REDFLAGS = [
  "Fever, or redness/discharge from the incision",
  "New or worsening numbness, weakness, or pain in the legs",
  "Difficulty controlling your bladder or bowel",
  "Severe pain not relieved by your prescribed medication",
];

const SectionTitle = ({ children }) => <h2 className="font-display font-light text-3xl md:text-4xl text-[#161C22]">{children}</h2>;
const BulletList = ({ items }) => (
  <ul className="space-y-3">
    {items.map((item, i) => (
      <li key={i} className="flex gap-3 text-[#161C22]/75 leading-relaxed">
        <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#5ECCA0] flex-shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);
const NumberedList = ({ items }) => (
  <ol className="space-y-4">
    {items.map((item, i) => (
      <li key={i} className="flex gap-4 text-[#161C22]/75 leading-relaxed">
        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#E3F0EA] text-[#1F3864] text-sm font-medium flex items-center justify-center font-mono-ui">{i + 1}</span>
        <span className="pt-0.5">{item}</span>
      </li>
    ))}
  </ol>
);
const Callout = ({ title, children }) => (
  <div className="rounded-xl border p-6 bg-[#E3F0EA] border-[#5ECCA0]/40">
    <div className="text-sm font-medium mb-1.5 text-[#1F3864]">{title}</div>
    <p className="text-sm leading-relaxed text-[#161C22]/70">{children}</p>
  </div>
);

export default function OpenTLIF() {
  const [faqs, setFaqs] = useState([]);
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Open TLIF Surgery in Bhubaneswar — Dr. Amit Chanduka";
    return () => { document.title = prevTitle; };
  }, []);
  useEffect(() => {
    axios.get(`${API}/specialization-faqs`, { params: { slug: "open-tlif" } }).then((res) => setFaqs(res.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-[#F3F5F4] min-h-screen" data-testid="open-tlif-page">
      <script type="application/ld+json">{JSON.stringify(SCHEMA)}</script>
      <SpineRail />
      <Nav />
      <main>
        <section className="pt-32 md:pt-40 pb-16 md:pb-20 bg-[#1F3864] text-white">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <a href="/#specializations" className="inline-flex items-center gap-2 text-white/60 hover:text-[#5ECCA0] text-sm mb-8 transition-colors">
                <ArrowLeft size={16} /> Back to Specializations
              </a>
              <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center text-[#5ECCA0] mb-6">
                <Maximize2 size={26} strokeWidth={1.5} />
              </div>
              <Eyebrow tone="onDark">Specialization</Eyebrow>
              <h1 className="font-display font-light text-5xl md:text-6xl mt-5 leading-tight">Open TLIF</h1>
              <p className="text-white/70 mt-3 text-lg md:text-xl">The traditional approach to TLIF — a guide for patients and families</p>
              <p className="text-white/60 mt-6 max-w-xl leading-relaxed">
                A guide by Dr. Amit Chanduka, Neurosurgeon &amp; Spine Surgeon, explaining Open TLIF, when it's the right
                choice, and what to expect before, during, and after surgery.
              </p>
              <a href="/#contact" className="inline-flex items-center rounded-full text-sm px-6 py-3 mt-8 bg-[#5ECCA0] text-[#14263D] hover-lift hover:shadow-lg hover:shadow-[#5ECCA0]/25 transition-colors">
                Book Consultation
              </a>
            </Reveal>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>What Is Open TLIF?</SectionTitle>
              <p className="text-[#161C22]/75 mt-5 leading-relaxed">
                Open TLIF (Transforaminal Lumbar Interbody Fusion) is the traditional, most established way of
                performing this operation. Dr. Chanduka makes a single incision along the middle of the lower back,
                and the back muscles are carefully moved aside to give a full, direct view of the affected level of
                the spine.
              </p>
              <p className="text-[#161C22]/75 mt-5 leading-relaxed">
                This wide, direct visualization is the main advantage of the open approach — it lets the surgeon see
                and treat the nerve, disc and bone with complete clarity, which is especially valuable in more
                complex cases. The goals are the same as any TLIF: remove the damaged disc, restore proper spacing,
                and fuse the two vertebrae into one solid, stable segment.
              </p>
              <div className="mt-6">
                <Callout title="The original approach">
                  Open TLIF is the original, time-tested version of this surgery. Newer techniques (MIS and Endo
                  TLIF) aim to reach the same result through smaller openings — but open surgery remains the most
                  reliable choice when the anatomy calls for a wider view.
                </Callout>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>When Is Open TLIF Recommended?</SectionTitle>
              <p className="text-[#161C22]/75 mt-5 leading-relaxed">
                Dr. Chanduka recommends the open approach specifically when a wide, direct view of the spine offers
                a real safety or accuracy advantage. This typically includes:
              </p>
              <div className="mt-6"><BulletList items={REASONS} /></div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>What Happens During the Procedure</SectionTitle>
              <div className="mt-6"><NumberedList items={STEPS} /></div>
              <div className="flex items-center gap-2.5 mt-8 text-[#161C22]/60 text-sm">
                <Clock size={16} className="text-[#5ECCA0]" />
                The surgery usually takes between two and a half and four hours, depending on how many levels are
                involved and the complexity of the case.
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>Preparing for Your Surgery</SectionTitle>
              <div className="mt-6"><BulletList items={BEFORE} /></div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>Recovery: What to Expect</SectionTitle>
              <p className="text-[#161C22]/75 mt-5 leading-relaxed">
                Because open surgery involves more muscle handling than minimally invasive techniques, initial
                recovery is a little slower — but the long-term outcome is just as strong.
              </p>
              <div className="mt-6 space-y-4">
                {RECOVERY.map(([stage, text]) => (
                  <div key={stage} className="bg-white rounded-xl border border-[#DCE3E0] p-5">
                    <div className="font-medium text-[#1F3864] text-sm mb-1">{stage}</div>
                    <p className="text-sm text-[#161C22]/70 leading-relaxed">{text}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>Risks and When to Call Us</SectionTitle>
              <p className="text-[#161C22]/75 mt-5 leading-relaxed">
                Open TLIF is a well-established procedure with a long track record. As with any spine surgery,
                possible risks include infection, bleeding, nerve irritation, more post-operative muscle soreness
                than minimally invasive approaches, or the fusion taking longer than expected to solidify.
              </p>
              <p className="text-[#161C22] font-medium mt-8 mb-4">Please contact the clinic promptly if, after surgery, you notice:</p>
              <div className="rounded-xl border border-red-200 bg-red-50 p-6">
                <ul className="space-y-3">
                  {REDFLAGS.map((item, i) => (
                    <li key={i} className="flex gap-3 text-red-700/90 leading-relaxed text-sm">
                      <AlertTriangle size={16} className="flex-shrink-0 mt-0.5 text-red-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </section>

        {faqs.length > 0 && (
          <section className="py-16 md:py-20">
            <div className="max-w-[900px] mx-auto px-5 md:px-8">
              <Reveal>
                <SectionTitle>Frequently Asked Questions</SectionTitle>
                <Accordion type="single" collapsible className="mt-8">
                  {faqs.map((item) => (
                    <AccordionItem key={item.id} value={item.id} className="border-[#DCE3E0]">
                      <AccordionTrigger className="text-left font-display text-lg text-[#161C22] hover:no-underline">{item.question}</AccordionTrigger>
                      <AccordionContent className="text-[#161C22]/70 leading-relaxed">{item.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            </div>
          </section>
        )}

        <section className="py-16 md:py-20 bg-[#1F3864] text-white text-center">
          <div className="max-w-[700px] mx-auto px-5 md:px-8">
            <Reveal>
              <CheckCircle2 size={28} className="text-[#5ECCA0] mx-auto mb-5" />
              <h2 className="font-display font-light text-3xl md:text-4xl">Have questions about Open TLIF?</h2>
              <p className="text-white/70 mt-4 leading-relaxed">
                This page is for general education and does not replace a personal consultation. Please discuss
                how this applies to your own case with Dr. Chanduka before making any treatment decision.
              </p>
              <a href="/#contact" className="inline-flex items-center rounded-full text-sm px-6 py-3 mt-8 bg-[#5ECCA0] text-[#14263D] hover-lift hover:shadow-lg hover:shadow-[#5ECCA0]/25 transition-colors">
                Book Consultation
              </a>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
